import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import { ROUTES } from '@/utils/constants';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';
import { staggerContainer, staggerItem } from '@/utils/animations';
import {
  UsersIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  TagIcon,
  CogIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  total_users: number;
  total_orders: number;
  total_revenue: number;
  total_shops: number;
  pending_orders: number;
  pending_shops: number;
  pending_products: number;
  low_stock_products: number;
  revenue_growth: number;
  orders_growth: number;
  users_growth: number;
}

interface RecentActivity {
  id: number;
  type: 'order' | 'shop' | 'product' | 'user';
  description: string;
  timestamp: string;
  status: 'pending' | 'approved' | 'rejected';
}

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isSuperAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current role from URL, localStorage, or path
  const getCurrentRole = () => {
    let currentRole = searchParams.get('role');
    
    if (!currentRole) {
      const savedRole = localStorage.getItem('selectedRole');
      if (savedRole) {
        currentRole = savedRole;
      }
    }
    
    if (!currentRole) {
      if (location.pathname.startsWith('/dashboard/super-admin')) {
        currentRole = 'super_admin';
      } else if (location.pathname.startsWith('/dashboard/admin')) {
        currentRole = 'admin';
      } else if (location.pathname.startsWith('/dashboard/staff')) {
        currentRole = 'staff';
      }
    }
    
    return currentRole || user?.role || 'admin';
  };

  useEffect(() => {
    fetchDashboardData();
  }, [location.pathname, searchParams]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const currentRole = getCurrentRole();
      let statsResponse;
      
      // Fetch dashboard stats based on current role context
      if (currentRole === 'super_admin') {
        statsResponse = await adminApi.getSuperAdminDashboard();
      } else if (currentRole === 'staff') {
        statsResponse = await adminApi.getStaffDashboard();
      } else {
        statsResponse = await adminApi.getAdminDashboard();
      }

      setStats(statsResponse.data);

      // Convert recent orders to activity format (if available)
      const recentOrders = statsResponse.data.recent_orders || [];
      const activities = recentOrders.map((order: any) => ({
        id: order.id,
        type: 'order',
        description: `New order ${order.order_number} placed by Customer #${order.buyer_id}`,
        timestamp: order.created_at,
        status: order.order_status === 'pending' ? 'pending' : 'approved'
      }));
      
      // If no recent orders, set empty array
      setRecentActivity(activities);

    } catch (error: any) {
      // Safely extract error message
      let errorMessage = 'Failed to fetch dashboard data';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          errorMessage = String(error.detail);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // If it's a credentials error, suggest refreshing token
      if (errorMessage.includes('credentials') || errorMessage.includes('401')) {
        errorMessage = 'Session expired. Please refresh the page or log in again.';
      }
      
      setError(errorMessage);
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <ShoppingBagIcon className="h-5 w-5 text-blue-500" />;
      case 'shop':
        return <BuildingStorefrontIcon className="h-5 w-5 text-green-500" />;
      case 'product':
        return <ChartBarIcon className="h-5 w-5 text-purple-500" />;
      case 'user':
        return <UsersIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 border border-warning-200 dark:border-warning-800';
      case 'approved':
        return 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-800';
      case 'rejected':
        return 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400 border border-error-200 dark:border-error-800';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 dark:border-primary-900 border-t-primary-600 dark:border-t-primary-400 mx-auto"></div>
          <p className="mt-6 text-gray-600 dark:text-gray-400 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" variants={staggerContainer}>
      {/* Header */}
      <motion.div variants={staggerItem} className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Welcome back, {user?.first_name}! Here's what's happening on your platform.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="md"
              onClick={fetchDashboardData}
              leftIcon={<ArrowPathIcon className="h-5 w-5" />}
            >
              Refresh Data
            </Button>
            {isSuperAdmin() && (
              <Button
                size="md"
                onClick={() => navigate('/super-admin/settings')}
                leftIcon={<CogIcon className="h-5 w-5" />}
              >
                System Settings
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div variants={staggerItem} className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg mb-6">
          <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
        </motion.div>
      )}

      {/* Stats Grid */}
      {stats && (
        <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats?.total_users?.toLocaleString() || '0'}
                </p>
                <div className="flex items-center mt-3">
                  {(stats?.users_growth || 0) >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', (stats?.users_growth || 0) >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats?.users_growth || 0)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <UsersIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </div>

          {/* Total Orders */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats?.total_orders?.toLocaleString() || '0'}
                </p>
                <div className="flex items-center mt-3">
                  {(stats?.orders_growth || 0) >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', (stats?.orders_growth || 0) >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats?.orders_growth || 0)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-success-100 dark:bg-success-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <ShoppingBagIcon className="h-7 w-7 text-success-600 dark:text-success-400" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  ${stats?.total_revenue?.toLocaleString() || '0'}
                </p>
                <div className="flex items-center mt-3">
                  {(stats?.revenue_growth || 0) >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', (stats?.revenue_growth || 0) >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats?.revenue_growth || 0)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-accent-100 dark:bg-accent-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <CurrencyDollarIcon className="h-7 w-7 text-accent-600 dark:text-accent-400" />
              </div>
            </div>
          </div>

          {/* Total Shops */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Shops</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats?.total_shops?.toLocaleString() || '0'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                  {stats?.pending_shops || 0} pending approval
                </p>
              </div>
              <div className="p-3 bg-info-100 dark:bg-info-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <BuildingStorefrontIcon className="h-7 w-7 text-info-600 dark:text-info-400" />
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions & Alerts */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(() => {
                  const currentRole = getCurrentRole();
                  const roleParam = `?role=${currentRole}`;
                  
                  // Determine base path based on role
                  const getBasePath = () => {
                    if (currentRole === 'super_admin') return '/dashboard/super-admin';
                    if (currentRole === 'admin') return '/dashboard/super-admin'; // Admin uses same routes
                    if (currentRole === 'staff') return '/dashboard/staff';
                    return '/dashboard/super-admin';
                  };
                  
                  const basePath = getBasePath();
                  
                  return (
                    <>
                      {/* Manage Users - Only for super_admin and admin */}
                      {(currentRole === 'super_admin' || currentRole === 'admin') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/users${roleParam}`)}
                        >
                          <UsersIcon className="h-6 w-6 mb-2" />
                          Manage Users
                        </Button>
                      )}
                      
                      {/* Manage Shops - Only for super_admin and admin */}
                      {(currentRole === 'super_admin' || currentRole === 'admin') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/shops${roleParam}`)}
                        >
                          <BuildingStorefrontIcon className="h-6 w-6 mb-2" />
                          Manage Shops
                        </Button>
                      )}
                      
                      {/* Moderate Products - All roles */}
                      <Button 
                        variant="outline" 
                        className="h-20 flex-col"
                        onClick={() => {
                          if (currentRole === 'staff') {
                            navigate(`/dashboard/staff/products${roleParam}`);
                          } else {
                            navigate(`${basePath}/products${roleParam}`);
                          }
                        }}
                      >
                        <ChartBarIcon className="h-6 w-6 mb-2" />
                        Moderate Products
                      </Button>
                      
                      {/* Manage Orders - Only for super_admin and admin */}
                      {(currentRole === 'super_admin' || currentRole === 'admin') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/orders${roleParam}`)}
                        >
                          <ShoppingBagIcon className="h-6 w-6 mb-2" />
                          Manage Orders
                        </Button>
                      )}
                      
                      {/* View Analytics - Only for super_admin and admin */}
                      {(currentRole === 'super_admin' || currentRole === 'admin') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/analytics${roleParam}`)}
                        >
                          <ChartBarIcon className="h-6 w-6 mb-2" />
                          View Analytics
                        </Button>
                      )}
                      
                      {/* Manage Categories - For super_admin, admin, and staff */}
                      {(currentRole === 'super_admin' || currentRole === 'admin' || currentRole === 'staff') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/categories${roleParam}`)}
                        >
                          <TagIcon className="h-6 w-6 mb-2" />
                          Manage Categories
                        </Button>
                      )}
                      
                      {/* Manage Brands - For super_admin, admin, and staff */}
                      {(currentRole === 'super_admin' || currentRole === 'admin' || currentRole === 'staff') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/brands${roleParam}`)}
                        >
                          <TagIcon className="h-6 w-6 mb-2" />
                          Manage Brands
                        </Button>
                      )}
                      
                      {/* Product Field Configuration - For super_admin and admin */}
                      {(currentRole === 'super_admin' || currentRole === 'admin') && (
                        <Button 
                          variant="outline" 
                          className="h-20 flex-col"
                          onClick={() => navigate(`${basePath}/product-fields${roleParam}`)}
                        >
                          <CogIcon className="h-6 w-6 mb-2" />
                          Field Config
                        </Button>
                      )}
                      
                      {/* Staff-specific actions */}
                      {currentRole === 'staff' && (
                        <>
                          <Button 
                            variant="outline" 
                            className="h-20 flex-col"
                            onClick={() => navigate(`/dashboard/staff/reviews${roleParam}`)}
                          >
                            <ChartBarIcon className="h-6 w-6 mb-2" />
                            Moderate Reviews
                          </Button>
                          <Button 
                            variant="outline" 
                            className="h-20 flex-col"
                            onClick={() => navigate(`/dashboard/staff/support${roleParam}`)}
                          >
                            <UsersIcon className="h-6 w-6 mb-2" />
                            User Support
                          </Button>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

        {/* System Alerts */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">System Alerts</h2>
            <div className="space-y-3">
              {stats?.pending_orders > 0 && (
                <div className="flex items-center gap-3 p-3 bg-warning-50 dark:bg-warning-900/10 border border-warning-200 dark:border-warning-800 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats?.pending_orders || 0} Pending Orders</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Requires attention</p>
                  </div>
                </div>
              )}

              {stats?.pending_shops > 0 && (
                <div className="flex items-center gap-3 p-3 bg-info-50 dark:bg-info-900/10 border border-info-200 dark:border-info-800 rounded-lg">
                  <BuildingStorefrontIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats?.pending_shops || 0} Pending Shops</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Awaiting approval</p>
                  </div>
                </div>
              )}

              {stats?.pending_products > 0 && (
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800 rounded-lg">
                  <ChartBarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats?.pending_products || 0} Pending Products</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Need review</p>
                  </div>
                </div>
              )}

              {stats?.low_stock_products > 0 && (
                <div className="flex items-center gap-3 p-3 bg-error-50 dark:bg-error-900/10 border border-error-200 dark:border-error-800 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-error-600 dark:text-error-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats?.low_stock_products || 0} Low Stock Items</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Inventory alert</p>
                  </div>
                </div>
              )}

              {(!stats?.pending_orders && !stats?.pending_shops && !stats?.pending_products && !stats?.low_stock_products) && (
                <div className="flex items-center gap-3 p-3 bg-success-50 dark:bg-success-900/10 border border-success-200 dark:border-success-800 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">All Systems Normal</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">No pending items</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div variants={staggerItem} className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Recent Activity</h2>
        <div className="space-y-3">
          {recentActivity.map((activity) => (
            <div
              key={activity.id}
              className="flex items-center gap-4 p-4 border border-gray-200 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
            >
              <div className="flex-shrink-0">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{activity.description}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(activity.timestamp).toLocaleString()}
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className={cn('px-3 py-1 rounded-full text-xs font-medium', getStatusColor(activity.status))}>
                  {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminDashboard;
